/* eslint-disable camelcase */

import {
  Box, DialogActions, Button, Stack, DialogContent,
} from '@mui/material';
import styled from '@emotion/styled';
import Divider from '@mui/material/Divider';
import { useState } from 'react';
import { useModalStore } from 'common/hooks/useModals';
import { serializeFormData } from 'common/util/serializeFormData';
import { useAddAircraftToAccount } from 'aircraft/hooks/useAddAircraftToAccount';
import { MdOutlineClose } from 'react-icons/md';
import AddAircraft from 'aircraft/components/AddAircraftModal/AddAircraft';
import { CanAddToAccountProvider, useCanAddToAccount } from 'aircraft/providers/CanAddToAccount';

const CreateBilledAccountHeader = styled.div`
    font-weight: bold;
    font-size: 24px;
    padding: 12px;
  `;

const ModalDivider = styled(Divider)`
`;

const ActionButton = styled(Button)`
    font-size: 10px;
`;

const ModalHeaderActionContainer = styled(Stack)`
  justify-content: space-between;
`;

const CloseButton = styled(Button)`
  font-size: 20px;
  color: black;
`;

const SubmitButton = styled(ActionButton)`
  width: 120px;
`;

const WarningNotice = styled.div`
  color: red;
  font-size: 12px;
  padding-top: 20px;
  text-align: right;
  width: 100%;
`;

function View({
  onSubmit,
  handleClose,
  showAlreadyRegistered,
  disableSubmit,
  userOptInAddExisting,
  onUserOptInAddExisting,
  handleValidationChange,
  companyId,
  accountName,
}) {
  return (
    <form onSubmit={onSubmit}>
      <ModalHeaderActionContainer spacing={2} direction="row">
        <CreateBilledAccountHeader>
          Create Aircraft
        </CreateBilledAccountHeader>
        <CloseButton variant="text" onClick={handleClose}>
          <MdOutlineClose />
        </CloseButton>
      </ModalHeaderActionContainer>
      <ModalDivider component="div" />
      <DialogContent>
        <AddAircraft
          onValidationChange={handleValidationChange}
          userOptInAddExisting={userOptInAddExisting}
          onUserOptInAddExisting={onUserOptInAddExisting}
          companyId={companyId}
          accountName={accountName}
        />
        {showAlreadyRegistered && (
          <WarningNotice>
            This Aircraft is already registered.
          </WarningNotice>
        )}
      </DialogContent>
      <ModalDivider component="div" />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box />
        <Stack spacing={2} direction="row">
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <SubmitButton disabled={disableSubmit} variant="contained" color="primary" type="submit">
            Create
          </SubmitButton>
        </Stack>
      </DialogActions>
    </form>
  );
}

function Model() {
  const { canAddToAccount, canSubmit, showExistingAircraftNotice } = useCanAddToAccount();
  const { mutation: addAircraftToAccountMutation } = useAddAircraftToAccount();
  const [errorValidation, hasErrorValidation] = useState('');
  const [userOptInAddExisting, setUserOptInAddExisting] = useState(false);

  const {
    addAircraft,
    setAddAircraftState,
  } = useModalStore();

  const {
    company_id,
    account_name,
  } = addAircraft;

  const onSubmit = async (e) => {
    e.preventDefault();

    const {
      reg_n_number, serial_number, make, model,
    } = serializeFormData(e);
    addAircraftToAccountMutation({
      reg_n_number,
      serial_number,
      make,
      model,
      company_id,
      account_name,
    });
  };

  const handleClose = () => {
    setAddAircraftState(false);
  };

  const handleValidationChange = (hasError) => {
    hasErrorValidation(hasError);
  };

  const handleOptInAddExisting = (e, optIn) => {
    setUserOptInAddExisting(optIn);
  };

  const hookProps = {
    onSubmit,
    handleClose,
    showAlreadyRegistered: canAddToAccount.registeredByThisCompany,
    disableSubmit: !canSubmit || errorValidation !== '' || (!userOptInAddExisting && showExistingAircraftNotice),
    handleValidationChange,
    userOptInAddExisting,
    onUserOptInAddExisting: handleOptInAddExisting,
    companyId: company_id,
    accountName: account_name,
  };

  return (
    <View
      {...hookProps}
    />
  );
}

function ProvidedModel() {
  return (
    <CanAddToAccountProvider>
      <Model />
    </CanAddToAccountProvider>
  );
}

export default ProvidedModel;
export { View };
